
import { useState, useEffect } from "react";
import { getSignedUrl } from "@/lib/storage";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Dumbbell, AlertTriangle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/components/AuthProvider";

interface ExerciseImageProps {
  imagePath?: string;
  altText?: string;
  fallbackText?: string;
  className?: string;
  isCustomExercise?: boolean;
}

export const ExerciseImage = ({ 
  imagePath, 
  altText = "Exercício", 
  fallbackText,
  className = "",
  isCustomExercise = false
}: ExerciseImageProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [authError, setAuthError] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const getExerciseBackgroundColor = (exerciseName: string) => {
    const colorMap: Record<string, string> = {
      "Agachamento": "bg-red-100",
      "Supino": "bg-blue-100",
      "Levantamento terra": "bg-green-100",
      "Barra fixa": "bg-yellow-100",
      "Rosca direta": "bg-purple-100",
      "Tríceps corda": "bg-indigo-100",
      "Prancha": "bg-orange-100",
      "Corrida": "bg-teal-100",
      "Esteira": "bg-pink-100",
      "Bicicleta": "bg-cyan-100",
    };
    
    const firstWord = exerciseName.split(' ')[0];
    if (colorMap[firstWord]) {
      return colorMap[firstWord];
    }
    
    const hash = exerciseName.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    const colors = [
      'bg-red-50', 'bg-orange-50', 'bg-amber-50', 'bg-yellow-50', 
      'bg-lime-50', 'bg-green-50', 'bg-emerald-50', 'bg-teal-50', 
      'bg-cyan-50', 'bg-sky-50', 'bg-blue-50', 'bg-indigo-50', 
      'bg-violet-50', 'bg-purple-50', 'bg-fuchsia-50', 'bg-pink-50', 
      'bg-rose-50'
    ];
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  useEffect(() => {
    let isMounted = true;
    
    const loadImageUrl = async () => {
      if (!imagePath) {
        console.log("No image path provided");
        setLoading(false);
        setError(true);
        return;
      }

      try {
        if (imagePath.startsWith('http')) {
          console.log("Using direct URL:", imagePath);
          setImageUrl(imagePath);
          setLoading(false);
          return;
        }
        
        if (!user) {
          console.log("Usuário não autenticado ao tentar carregar imagem:", imagePath);
          setAuthError(true);
          setLoading(false);
          return;
        }

        const bucket = isCustomExercise ? 'exercise-images' : 'exercise-gifs';
        console.log(`Loading image for ${isCustomExercise ? 'custom' : 'standard'} exercise from ${bucket}:`, imagePath);
        console.log("isCustomExercise value:", isCustomExercise);
        
        const url = await getSignedUrl(imagePath, bucket);
        
        if (isMounted) {
          if (!url) {
            console.error("Failed to get URL for:", imagePath);
            setError(true);
            setLoading(false);
          } else {
            console.log("Successfully loaded image URL:", url);
            setImageUrl(url);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Error loading image:", error);
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    setLoading(true);
    setError(false);
    setImageUrl(null);
    
    const timer = setTimeout(() => {
      loadImageUrl();
    }, 50);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [imagePath, isCustomExercise, user]);

  if (loading) {
    return (
      <div className={`rounded-md overflow-hidden ${className}`}>
        <AspectRatio ratio={1/1}>
          <Skeleton className="w-full h-full" />
        </AspectRatio>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="bg-red-50 rounded-md overflow-hidden flex flex-col items-center justify-center p-4 text-center">
        <AspectRatio ratio={1/1}>
          <div className="w-full h-full flex flex-col items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mb-2" />
            <p className="text-xs text-red-600">Autenticação necessária para visualizar</p>
          </div>
        </AspectRatio>
      </div>
    );
  }

  if (error || !imageUrl) {
    const displayText = fallbackText || altText;
    return (
      <div className={`${getExerciseBackgroundColor(displayText)} rounded-md overflow-hidden flex items-center justify-center ${className}`}>
        <AspectRatio ratio={1/1}>
          <div className="w-full h-full flex flex-col items-center justify-center p-2">
            <Dumbbell className="h-8 w-8 text-flame/60 mb-2" />
            <p className="text-xs text-center font-medium mt-2">{displayText}</p>
          </div>
        </AspectRatio>
      </div>
    );
  }

  return (
    <div 
      className={`rounded-md overflow-hidden select-none ${className}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <AspectRatio ratio={1/1}>
        <img 
          src={imageUrl} 
          alt={altText}
          className="w-full h-full object-contain pointer-events-none"
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          style={{
            WebkitUserSelect: 'none',
            userSelect: 'none',
            WebkitTouchCallout: 'none'
          }}
          onError={(e) => {
            console.error("Erro ao carregar imagem:", imageUrl);
            setError(true);
          }}
        />
      </AspectRatio>
    </div>
  );
}
